import { toPacificDateKey } from './report-events.ts';

export interface ReportPayoutRow {
  id: string | null;
  organization_id: string | null;
  encompass_loan_id: string | null;
  loan_number: string | null;
  fund_date: string | null;
}

export interface PayoutMatchableLoan {
  encompass_loan_id: string;
  trans_details_loan_number: string | null;
  broker_fund_date?: string | null;
  organization_id?: string | null;
}

export type PayoutMatchSource = 'encompass_loan_id' | 'organization_loan_number' | null;

export interface PayoutConflictWarning {
  keyType: Exclude<PayoutMatchSource, null>;
  key: string;
  dates: string[];
  selectedDate: string;
}

export interface PayoutFundDateIndex {
  byEncompassLoanId: ReadonlyMap<string, string>;
  byOrganizationLoanNumber: ReadonlyMap<string, string>;
  warnings: PayoutConflictWarning[];
}

export interface PayoutMatchResult<TLoan extends PayoutMatchableLoan> {
  loans: TLoan[];
  warnings: PayoutConflictWarning[];
  matches: ReadonlyMap<string, PayoutMatchSource>;
}

export function normalizeOrganizationId(value: string | null | undefined): string | null {
  const normalized = value?.trim().toLowerCase() || '';
  return normalized || null;
}

export function normalizeLoanNumber(value: string | null | undefined): string | null {
  const normalized = value?.trim().toUpperCase().replace(/[^A-Z0-9]/g, '') || '';
  return normalized || null;
}

export function organizationLoanNumberKey(
  organizationId: string | null | undefined,
  loanNumber: string | null | undefined
): string | null {
  const organization = normalizeOrganizationId(organizationId);
  const loan = normalizeLoanNumber(loanNumber);
  return organization && loan ? `${organization}::${loan}` : null;
}

function normalizeEncompassLoanId(value: string | null | undefined): string | null {
  const normalized = value?.trim() || '';
  return normalized || null;
}

function buildDateMap(
  rows: ReportPayoutRow[],
  keyType: Exclude<PayoutMatchSource, null>,
  getKey: (row: ReportPayoutRow) => string | null
): { map: Map<string, string>; warnings: PayoutConflictWarning[] } {
  const dateSets = new Map<string, Set<string>>();

  rows.forEach((row) => {
    const key = getKey(row);
    const date = toPacificDateKey(row.fund_date);
    if (!key || !date) {
      return;
    }
    const dates = dateSets.get(key) || new Set<string>();
    dates.add(date);
    dateSets.set(key, dates);
  });

  const map = new Map<string, string>();
  const warnings: PayoutConflictWarning[] = [];
  Array.from(dateSets.keys()).sort().forEach((key) => {
    const dates = Array.from(dateSets.get(key) || []).sort();
    const selectedDate = dates[0];
    if (!selectedDate) {
      return;
    }
    map.set(key, selectedDate);
    if (dates.length > 1) {
      warnings.push({ keyType, key, dates, selectedDate });
    }
  });

  return { map, warnings };
}

/** Build deterministic payout indexes without filtering by payout metadata. */
export function buildPayoutFundDateIndex(rows: ReportPayoutRow[]): PayoutFundDateIndex {
  const byId = buildDateMap(rows, 'encompass_loan_id', (row) => normalizeEncompassLoanId(row.encompass_loan_id));
  const byNumber = buildDateMap(
    rows,
    'organization_loan_number',
    (row) => organizationLoanNumberKey(row.organization_id, row.loan_number)
  );

  return {
    byEncompassLoanId: byId.map,
    byOrganizationLoanNumber: byNumber.map,
    warnings: [...byId.warnings, ...byNumber.warnings]
  };
}

/**
 * Attach the canonical broker payout date. Stable Encompass ID wins; the
 * organization-scoped normalized loan number is only a fallback.
 */
export function attachCanonicalPayoutDates<TLoan extends PayoutMatchableLoan>(
  loans: TLoan[],
  payouts: ReportPayoutRow[]
): PayoutMatchResult<TLoan> {
  const index = buildPayoutFundDateIndex(payouts);
  const matches = new Map<string, PayoutMatchSource>();
  const mergedLoans = loans.map((loan) => {
    const encompassId = normalizeEncompassLoanId(loan.encompass_loan_id);
    const organizationNumber = organizationLoanNumberKey(
      loan.organization_id,
      loan.trans_details_loan_number
    );
    const idDate = encompassId ? index.byEncompassLoanId.get(encompassId) : null;
    const numberDate = organizationNumber
      ? index.byOrganizationLoanNumber.get(organizationNumber)
      : null;
    const brokerFundDate = idDate || numberDate || null;
    const matchSource: PayoutMatchSource = idDate
      ? 'encompass_loan_id'
      : numberDate
        ? 'organization_loan_number'
        : null;

    matches.set(loan.encompass_loan_id, matchSource);
    return {
      ...loan,
      broker_fund_date: brokerFundDate
    };
  });

  return {
    loans: mergedLoans,
    warnings: index.warnings,
    matches
  };
}
